import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { filter, timeout } from 'rxjs/operators';
import { AccountState } from 'src/app/store/account/account.state';

/**
 * Waits for AccountState to be fully initialized before the guard reads any
 * signal values. Mirrors the pattern used by the other async account guards.
 */
async function waitForAccountInitialized(accountState: AccountState): Promise<void> {
  await firstValueFrom(
    toObservable(accountState.initialized).pipe(
      filter(initialized => initialized === true),
      timeout(10000),
    ),
  );
}

/**
 * Data Opt-In Guard
 *
 * Blocks the data-handling surfaces (notebook, admin) until the user has
 * accepted the current terms-of-service / privacy-policy version. When the
 * user has not opted in, they are redirected to `/my-blume`, where the
 * consent UI is rendered in place of the navigation tiles.
 *
 * `/my-blume` itself is intentionally NOT protected by this guard so the
 * acceptance surface stays reachable.
 *
 * @example
 * // In app.routes.ts
 * {
 *   path: 'notebook',
 *   canActivate: [authGuard, emailVerifiedGuard, profileGuard, dataOptInGuard, licenseGuardFor('tools')],
 *   loadComponent: () => import('./features/notebook/notebook.page')
 * }
 *
 * @returns true if the current terms are accepted, false otherwise (with redirect)
 */
export const dataOptInGuard: CanActivateFn = async () => {
  const accountState = inject(AccountState);
  const router = inject(Router);

  try {
    await waitForAccountInitialized(accountState);

    if (!accountState.hasAcceptedCurrentTerms()) {
      router.navigate(['/my-blume']);
      return false;
    }

    return true;
  } catch (error) {
    console.error('[DataOptInGuard] Error checking account:', error);
    router.navigate(['/my-blume']);
    return false;
  }
};
